import { apiClient } from '@/api/client';
import type { OnboardOrganizationValues } from '@/features/platform/schema';

export interface PlatformCapabilities {
  canOnboardOrganizations: boolean;
  canAccessMailboxInbox: boolean;
}

export interface OwnerWebmailSession {
  token: string;
  actionUrl: string;
  expiresIn: number;
}

export interface OnboardedOrganization {
  tenant: { id: string; slug: string };
  organization: { id: string; legalName: string; accountCode: string };
  adminInvitation?: { id: string; email: string; expiresAt: string; acceptUrl: string };
}

/** Platform-plane endpoints (organization-service via gateway). */
export const platformApi = {
  capabilities: async (): Promise<PlatformCapabilities> =>
    (await apiClient.get<PlatformCapabilities>('/platform/capabilities')).data,

  /** Platform-owner only. The returned token stays in memory and is POSTed once. */
  createOwnerWebmailSession: async (mailbox: { id: string; address: string }): Promise<OwnerWebmailSession> =>
    (await apiClient.post<OwnerWebmailSession>('/platform/webmail-sessions', {
      mailboxId: mailbox.id,
      address: mailbox.address,
    })).data,

  /** Accept an invitation as the signed-in user (must own the invited email). */
  acceptInvitation: async (token: string): Promise<{ tenantId: string }> =>
    (await apiClient.post<{ tenantId: string }>('/invitations/accept', { token })).data,

  /** Superadmin-only. Creates the organization + a pending admin invitation; sends no email. */
  onboardOrganization: async (body: OnboardOrganizationValues): Promise<OnboardedOrganization> =>
    (await apiClient.post<OnboardedOrganization>('/organizations', {
      legalName: body.legalName.trim(),
      slug: body.slug,
      adminEmail: body.adminEmail.trim().toLowerCase(),
      countryCode: body.countryCode,
    })).data,
};
